import { useActiveCamera } from "@/components/r3f/cameras/active-camera-context";
import { useCurrentClippingBox } from "@/tools/clip-scene-tool";
import {
  CameraDirection,
  CameraView,
  CameraViewType,
  SetCameraView,
} from "@/utils/cam-view";
import { useNonExhaustiveEffect } from "@faro-lotv/app-component-toolbox";
import {
  DEFAULT_PERSPECTIVE_FOV,
  DEG_TO_RAD,
  memberWithPrivateData,
  OrientedBoundingBox,
  WalkOrbitControls,
} from "@faro-lotv/lotv";
import { useThree } from "@react-three/fiber";
import { useCallback, useEffect, useState } from "react";
import { Box3, Matrix4, Object3D, Quaternion, Vector3 } from "three";

/**
 * Manages user requests to animate the camera to a predefined view: top, bottom, front, back, left, right.
 *
 * @param setCameraView Callback to animate the camera to a given view
 * @param areaBox The current area box
 * @param modelBox The bounding box of all models
 * @returns The clipping box around which the predefined views are centered
 */
export function useCameraPredefinedViews(
  setCameraView: SetCameraView,
  areaBox: OrientedBoundingBox | undefined,
  modelBox: Box3 | undefined,
): OrientedBoundingBox | undefined {
  const { predefinedView, cameraProjection } = useActiveCamera();

  // The clipping box is either the box saved in the store, or the bounding box of all models
  // rendered in overview mode
  const { storeClippingBox, initialClippingBox } = useCurrentClippingBox(
    areaBox,
    modelBox,
  );
  const aspectRatio = useThree((s) => s.size.width / s.size.height);

  const clippingBox = storeClippingBox ?? initialClippingBox;

  useNonExhaustiveEffect(() => {
    if (!predefinedView) return;

    const cameraView = computeCameraView(
      predefinedView,
      cameraProjection,
      clippingBox,
      aspectRatio,
    );
    setCameraView(cameraView);
  }, [predefinedView]);

  return clippingBox;
}

type SetContols = (controls: WalkOrbitControls | null) => void;

/**
 * When one of the six camera predefined views is selected, this hook starts
 * listening to the controls, and when the controls are moved again, it resets
 * the predefined view to 'undefined', so that the toolbar icon is restored to
 * its neutral state.
 *
 * @returns A function to set the controls to this hook
 */
export function useResetPredefinedViewWhenCameraMoves(): SetContols {
  const { predefinedView, setPredefinedView } = useActiveCamera();
  const [controls, setControls] = useState<WalkOrbitControls | null>(null);

  const viewChanged = useCallback(
    () => setPredefinedView(undefined),
    [setPredefinedView],
  );

  useEffect(() => {
    if (controls && predefinedView) {
      controls.userInteracted.on(viewChanged);
      return () => controls.userInteracted.off(viewChanged);
    }
  }, [controls, predefinedView, viewChanged]);

  return setControls;
}

const computeCameraView = memberWithPrivateData(() => {
  const M = new Matrix4();
  const boxSize = new Vector3();
  const viewDirection = new Vector3();
  const cameraScreenUp = new Vector3();
  const cameraScreenRight = new Vector3();

  return (
    predefinedView: CameraDirection,
    cameraProjection: CameraViewType,
    clippingBox: OrientedBoundingBox | undefined,
    aspectRatio: number,
  ): CameraView => {
    // Box that the camera has to frame. It is always aligned to the global CS
    const box = new Box3();

    if (clippingBox) {
      // projecting the current clipping box to the global CS
      M.compose(clippingBox.position, clippingBox.quaternion, clippingBox.size);
      box.expandByPoint(new Vector3(0.5, 0.5, 0.5));
      box.expandByPoint(new Vector3(-0.5, -0.5, -0.5));
      box.applyMatrix4(M);
    } else {
      // defaulting to a 20x20x20 box centered at the origin
      box.expandByScalar(10);
    }

    // Computing the target camera view, in terms of position, rotation, zoom level, and target
    const rotationMatrix = getLookAtMatrix(predefinedView);
    const rotation = new Quaternion().setFromRotationMatrix(rotationMatrix);
    // Setting the camera target (rotation pivot) to the center of the box
    const target = box.getCenter(new Vector3());
    box.getSize(boxSize);
    viewDirection.copy(CAMERA_VIEW_DIRECTIONS[predefinedView]);
    const sizeAlongDir = Math.abs(boxSize.dot(viewDirection));
    const e = rotationMatrix.elements;
    cameraScreenUp.set(e[4], e[5], e[6]);
    cameraScreenRight.set(e[0], e[1], e[2]);
    const sizeAlongCameraScreenUp =
      Math.max(
        Math.abs(boxSize.dot(cameraScreenUp)),
        Math.abs(boxSize.dot(cameraScreenRight)) / aspectRatio,
      ) * BBOX_PADDING_MULTIPLIER;
    // computing how far from the bounding box the camera has to be,
    // to frame the vertical size 'sizeAlongCameraScreenUp', assuming a
    // FOV of DEFAULT_PERSPECTIVE_FOV degrees.
    const distanceFromBox =
      sizeAlongCameraScreenUp * 0.5 * INVERSE_HALF_TAN_DEFAULT_FOV;
    const position = new Vector3().subVectors(
      target,
      viewDirection.multiplyScalar(distanceFromBox + sizeAlongDir / 2),
    );
    const zoomLevel =
      cameraProjection === CameraViewType.perspective
        ? DEFAULT_PERSPECTIVE_FOV
        : sizeAlongCameraScreenUp;
    return {
      type: cameraProjection,
      position,
      rotation,
      zoomLevel,
      target,
    };
  };
});

const INVERSE_HALF_TAN_DEFAULT_FOV =
  1.0 / Math.tan(DEFAULT_PERSPECTIVE_FOV * DEG_TO_RAD * 0.5);

const CAMERA_VIEW_DIRECTIONS: Record<CameraDirection, Vector3> = Object.freeze({
  top: new Vector3(0, -1, 0),
  bottom: new Vector3(0, 1, 0),
  front: new Vector3(0, 0, -1),
  back: new Vector3(0, 0, 1),
  left: new Vector3(1, 0, 0),
  right: new Vector3(-1, 0, 0),
});

function getLookAtMatrix(direction: CameraDirection): Matrix4 {
  const viewDir = CAMERA_VIEW_DIRECTIONS[direction];
  return new Matrix4().lookAt(new Vector3(), viewDir, Object3D.DEFAULT_UP);
}

const BBOX_PADDING_MULTIPLIER = 1.03;
